import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  adminGBPBusinessPaginationAction,
  GBPBusinessEnableAction,
  getCheckinBusinessPaginatedAction,
  getPostsPaginatedAction,
  GetGBPAction,
  GetGBPBusinessAction,
  GetGBPBusinessDetailsAction,
  GetListGBPPaginatedAction,
  IntigrationAction,
  listManagedBusinessAction,
  removeIntigrationAction,
} from "../../actions/AdminActions/GBPActions";
import {
  getGMBreviewsAction,
  getReviewsAction,
  getReviewSummaryAction,
  getReviewConfigAction,
  updateReviewConfigAction,
  toggleAutomationAction,
  listAgentsAction,
  createAgentAction,
  editAgentAction,
  deleteAgentAction,
} from "../../actions/MapCheckInDashboardAction";

interface ILocation {
  type: string;
  coordinates: number[];
}

interface ICategory {
  name: string;
  _id: string;
}

interface IIntegrationStatus {
  gbp: boolean;
  facebook?: boolean;
  instagram?: boolean;
  website?: boolean;
  youtube?: boolean;
  tiktok?: boolean;
  _id: string;
  id: string;
}

interface IGBPBusiness {
  location: ILocation;
  _id: string;
  added_by: string;
  subscription_id: string;
  expiry: string;
  token: string;
  email: string;
  business_name: string;
  address: string;
  place_id: string;
  url: string;
  rating: number;
  reviews: number;
  phone: string;
  website: string;
  categories: ICategory[];
  business_info: string;
  logo: string;
  status: "active" | "inactive";
  logo_placement: string;
  moments: any[];
  integrations: string[];
  integration_status: IIntegrationStatus;
  createdAt: string;
  updatedAt: string;
  __v: number;
  services: string[];
  timeZone: string;
  id: string;
}

interface IPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

interface GBPState {
  data: any[];
  allBusiness: any[];
  checkinBusinessPagination: IPagination;
  GBPBusiness: IGBPBusiness[];
  GBPBusinessPagination: IPagination;
  GBPBusinessLoading: boolean;
  GBPBusinessList: any;
  GBPBusinessListPagination: IPagination;
  GBPEmails: string[];
  loading: boolean;
  error: string | null;
  dashboarddata: any;
  dashboardPostsPagination: IPagination;
  selectedBusinessId: string | null;
  selectedBusiness: any;
  integrationDataLoading: boolean;
  removeIntegrationDataLoading: boolean;
  allreviews: any;
  reviewsData: any;
  reviewsMeta: any;
  reviewsPagination: any;
  reviewsLoading: boolean;
  reviewsSummary: any;
  reviewsSummaryLoading: boolean;
  reviewConfig: any;
  reviewConfigLoading: boolean;
  agentsList: any[];
  agentsLoading: boolean;
  statistics: any;
  employees: any[];
}

const initialState: GBPState = {
  data: [],
  allBusiness: [],
  checkinBusinessPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  GBPBusiness: [],
  GBPBusinessPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  GBPBusinessList: null,
  GBPBusinessListPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  GBPEmails: [],
  GBPBusinessLoading: false,
  loading: false,
  error: null,
  dashboarddata: null,
  dashboardPostsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  selectedBusinessId: null,
  selectedBusiness: null,
  integrationDataLoading: false,
  removeIntegrationDataLoading: false,
  allreviews: [],
  reviewsData: null,
  reviewsMeta: null,
  reviewsPagination: null,
  reviewsLoading: false,
  reviewsSummary: null,
  reviewsSummaryLoading: false,
  reviewConfig: null,
  reviewConfigLoading: false,
  agentsList: [],
  agentsLoading: false,
  statistics: null,
  employees: [],
};

const GBPSlice = createSlice({
  name: "gbp",
  initialState,
  reducers: {
    setSelectedBusiness: (state, action) => {
      state.selectedBusinessId = action.payload;
      state.selectedBusiness =
        state.allBusiness.find((b: any) => b._id === action.payload) ||
        state.data.find((b: any) => b._id === action.payload) ||
        null;
    },

    setClearGBPBusinessList: (state) => {
      state.GBPBusinessList = null;
    },

    updateCredits: (state, action: PayloadAction<number>) => {
      if (state.selectedBusiness) {
        state.selectedBusiness.credits = Math.max(
          0,
          (state.selectedBusiness.credits || 0) - action.payload,
        );
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(GetGBPBusinessDetailsAction.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated && updated._id) {
          if (state.selectedBusiness?._id === updated._id) {
            state.selectedBusiness = { ...state.selectedBusiness, ...updated };
          }
          if (Array.isArray(state.allBusiness)) {
            state.allBusiness = state.allBusiness.map((b: any) =>
              b._id === updated._id ? { ...b, ...updated } : b,
            );
          }
          if (Array.isArray(state.data)) {
            state.data = state.data.map((b: any) =>
              b._id === updated._id ? { ...b, ...updated } : b,
            );
          }
        }
      })
      .addCase(GetGBPAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetGBPAction.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        if (!state.allBusiness || state.allBusiness.length === 0) {
          state.allBusiness = action.payload.data;
        }
      })
      .addCase(GetGBPAction.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to load GBP data";
      })

      .addCase(getCheckinBusinessPaginatedAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCheckinBusinessPaginatedAction.fulfilled, (state, action) => {
        state.loading = false;
        state.allBusiness = action.payload.data;
        state.checkinBusinessPagination = action.payload.pagination;
        if (!state.data || state.data.length === 0) {
          state.data = action.payload.data?.filter(
            (item: any) => item?.status === "active",
          );
        }
      })
      .addCase(getCheckinBusinessPaginatedAction.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to load GBP data";
      })

      .addCase(listManagedBusinessAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listManagedBusinessAction.fulfilled, (state, action) => {
        state.loading = false;
        state.allBusiness = action.payload.data;
        state.data = action.payload.data?.filter(
          (item: any) => item?.status === "active",
        );
      })
      .addCase(listManagedBusinessAction.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to load GBP data";
      })

      .addCase(getGMBreviewsAction.pending, (state) => {
        state.allreviews = null;
      })
      .addCase(getGMBreviewsAction.fulfilled, (state, action) => {
        state.allreviews = action.payload.data;
      })
      .addCase(getGMBreviewsAction.rejected, (state, action) => {
        state.allreviews = null;
      })
      .addCase(getReviewsAction.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsData = null;
        state.reviewsMeta = null;
        state.reviewsPagination = null;
      })
      .addCase(getReviewsAction.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsData = action.payload?.data || [];
        state.reviewsMeta = action.payload?.meta || null;
        state.reviewsPagination = action.payload?.pagination || null;
      })
      .addCase(getReviewsAction.rejected, (state) => {
        state.reviewsLoading = false;
        state.reviewsData = null;
        state.reviewsMeta = null;
        state.reviewsPagination = null;
      })
      .addCase(getReviewSummaryAction.pending, (state) => {
        state.reviewsSummaryLoading = true;
      })
      .addCase(getReviewSummaryAction.fulfilled, (state, action) => {
        state.reviewsSummaryLoading = false;
        state.reviewsSummary = action.payload?.data || null;
      })
      .addCase(getReviewSummaryAction.rejected, (state) => {
        state.reviewsSummaryLoading = false;
        state.reviewsSummary = null;
      })
      .addCase(getReviewConfigAction.pending, (state) => {
        state.reviewConfigLoading = true;
      })
      .addCase(getReviewConfigAction.fulfilled, (state, action) => {
        state.reviewConfigLoading = false;
        state.reviewConfig = action.payload?.data || null;
      })
      .addCase(getReviewConfigAction.rejected, (state) => {
        state.reviewConfigLoading = false;
        state.reviewConfig = null;
      })
      .addCase(updateReviewConfigAction.fulfilled, (state, action) => {
        state.reviewConfig = action.payload?.data || state.reviewConfig;
      })
      .addCase(toggleAutomationAction.fulfilled, (state, action) => {
        if (state.reviewConfig) {
          state.reviewConfig.enabled =
            action.payload?.data?.enabled !== undefined
              ? action.payload.data.enabled
              : action.meta.arg.enabled;
        } else {
          state.reviewConfig = {
            enabled:
              action.payload?.data?.enabled !== undefined
                ? action.payload.data.enabled
                : action.meta.arg.enabled,
          };
        }
      })
      .addCase(listAgentsAction.pending, (state) => {
        state.agentsLoading = true;
      })
      .addCase(listAgentsAction.fulfilled, (state, action) => {
        state.agentsLoading = false;
        state.agentsList = action.payload?.data || [];
      })
      .addCase(listAgentsAction.rejected, (state) => {
        state.agentsLoading = false;
        state.agentsList = [];
      })
      .addCase(createAgentAction.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.agentsList.push(action.payload.data);
        }
      })
      .addCase(editAgentAction.fulfilled, (state, action) => {
        if (action.payload?.data) {
          const updatedAgent = action.payload.data;
          state.agentsList = state.agentsList.map((a) =>
            a._id === updatedAgent._id ? updatedAgent : a,
          );
        }
      })
      .addCase(deleteAgentAction.fulfilled, (state, action) => {
        const deletedId = action.meta.arg.agent_id;
        state.agentsList = state.agentsList.filter((a) => a._id !== deletedId);
      })

      // .addCase(GetDashboardAction.fulfilled, (state, action) => {
      //   state.dashboarddata = action.payload.data;
      // })
      .addCase(getPostsPaginatedAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPostsPaginatedAction.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboarddata = action.payload?.data;
        state.dashboardPostsPagination = action.payload?.pagination;
        state.statistics = action.payload?.statistics;
        state.employees = action.payload?.employees || [];
      })
      .addCase(getPostsPaginatedAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(GetGBPBusinessAction.pending, (state) => {
        state.GBPBusinessLoading = true;
        state.error = null;
      })
      .addCase(GetGBPBusinessAction.fulfilled, (state, action) => {
        state.GBPBusinessLoading = false;
        state.GBPBusiness = action.payload?.data;
        state.error = null;
      })
      //       .addCase(GetListGBPAction.pending, (state) => {
      //         state.GBPBusinessLoading = true;
      //         state.error = null;
      //       })
      //       .addCase(GetListGBPAction.fulfilled, (state, action) => {
      //         state.GBPBusinessLoading = false;
      //         state.GBPBusinessList = action.payload?.data;
      //         state.error = null;
      //       })
      .addCase(GetListGBPPaginatedAction.pending, (state) => {
        state.GBPBusinessLoading = true;
        state.error = null;
      })
      .addCase(GetListGBPPaginatedAction.fulfilled, (state, action) => {
        state.GBPBusinessLoading = false;
        state.GBPBusinessList = action.payload?.data;
        state.GBPBusinessListPagination = action.payload?.pagination;
        state.GBPEmails = action.payload?.emails || [];
        state.error = null;
      })
      .addCase(GetListGBPPaginatedAction.rejected, (state, action) => {
        state.GBPBusinessLoading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to load GBP data";
      })
      .addCase(GetGBPBusinessAction.rejected, (state, action) => {
        state.GBPBusinessLoading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to load GBP data";
      })
      .addCase(GBPBusinessEnableAction.fulfilled, (state, action) => {
        state.GBPBusiness = state.GBPBusiness.map((item) =>
          item.id === action.meta.arg ? { ...item, status: "active" } : item,
        );
      })

      .addCase(IntigrationAction.pending, (state) => {
        state.integrationDataLoading = true;
      })
      .addCase(IntigrationAction.fulfilled, (state, action) => {
        state.integrationDataLoading = false;
      })
      .addCase(IntigrationAction.rejected, (state, action) => {
        state.integrationDataLoading = false;
      })

      .addCase(removeIntigrationAction.pending, (state) => {
        state.removeIntegrationDataLoading = true;
      })
      .addCase(removeIntigrationAction.fulfilled, (state, action) => {
        state.removeIntegrationDataLoading = false;
      })
      .addCase(removeIntigrationAction.rejected, (state, action) => {
        state.removeIntegrationDataLoading = false;
      })

      // Admin GBP business pagination
      .addCase(adminGBPBusinessPaginationAction.pending, (state) => {
        state.GBPBusinessLoading = true;
      })
      .addCase(adminGBPBusinessPaginationAction.fulfilled, (state, action) => {
        state.GBPBusinessLoading = false;
        state.GBPBusiness = action.payload.data;
        state.GBPBusinessPagination = action.payload.pagination;
      })
      .addCase(adminGBPBusinessPaginationAction.rejected, (state, action) => {
        state.GBPBusinessLoading = false;
      });
  },
});

export const { setSelectedBusiness, updateCredits, setClearGBPBusinessList } =
  GBPSlice.actions;

export default GBPSlice.reducer;
